import styled from '@emotion/styled'
import _ from 'lodash'
import { useState } from 'react'

import {
  ApiFundOfFundsPortfolio,
  ApiModelPortfolio,
  ApiRebalancingDefinition,
  ApiSelfSelectPortfolio,
  ApiStrategyTemplate
} from '@/api'
import { MessageBox, ModalHeader, SimpleModal, Space, Tabs, Typography } from '@/components/core-v2'
import { PORTFOLIO_TYPE } from '@/constants'
import { formatUnits } from '@/utilities'
import { toleranceToPercentageString } from '@/utilities/available-tolerances'
import { useUser } from '@/utilities/hooks/useUser'

import { ProposedChangesApproval } from './proposed-changes-approval'
import { ProposedChangesModalAssetsSection } from './proposed-changes-modal-assets-section'
import { ProposedChangesModalDataBlock } from './proposed-changes-modal-data-block'
import { ProposedChangesModalFeesSection } from './proposed-changes-modal-fees-section'
import { ProposedChangesRestrictionsTabContent } from './proposed-changes-restrictions-tab-content'
import { StyledVerticalDivider } from './styled-vertical-divider'
import { RowData, SelectedTab } from './types'

type Portfolio = (ApiModelPortfolio | ApiFundOfFundsPortfolio | ApiSelfSelectPortfolio) | null

interface ProposedChangesModalProps {
  availableStrategies: ApiStrategyTemplate[]
  canProceedApproval: boolean
  currentPortfolio: Portfolio
  currentTotalValue: number
  onApprovePortfolio: () => void
  onChangeApprovalStatus: (status: string) => void
  onChangeApprovalUserName: (userName: string) => void
  onClose: () => void
  proposedPortfolio: Portfolio
}

const StyledMessageBoxWrapper = styled.div`
  .message-box-children-wrapper {
    display: flex;
    flex-grow: 1;
  }
`

const StyledChangesModalTabsWrapper = styled.div`
  & > div {
    display: flex;
    justify-content: center;
  }
`

export const ProposedChangesModal = ({
  availableStrategies,
  canProceedApproval,
  currentPortfolio: cPortfolio,
  currentTotalValue,
  onApprovePortfolio,
  onChangeApprovalStatus,
  onChangeApprovalUserName,
  onClose,
  proposedPortfolio: pPortfolio
}: ProposedChangesModalProps) => {
  const { data: user } = useUser()

  const [selectedTab, setSelectedTab] = useState<SelectedTab>('details')

  const currentPortfolio = _.cloneDeep(cPortfolio)
  const proposedPortfolio = _.cloneDeep(pPortfolio)

  const generateDataLists = (portfolio: Portfolio) => {
    const strategyList: RowData[] = availableStrategies.map(x => ({
      name: x.strategyName,
      value: !portfolio?.disallowedStrategies?.includes(x.strategyTemplateId),
      different: false
    }))

    const getRebalancingDisplayValue = (rebalancingDefinition: ApiRebalancingDefinition) => {
      if (!rebalancingDefinition?.type) return null

      if (rebalancingDefinition?.type === 'None') return 'None'

      const isFrequency =
        rebalancingDefinition?.type === 'Frequency' ||
        rebalancingDefinition?.type === 'CalendarFrequency'

      return isFrequency
        ? `${rebalancingDefinition?.type} - ${rebalancingDefinition?.frequency}`
        : `${rebalancingDefinition?.type} - ${toleranceToPercentageString(
            rebalancingDefinition?.weightsTolerance
          )}`
    }

    const propertyList: RowData[] = [
      { name: 'Multiplier', value: portfolio?.multiplier ?? '-', different: false },
      {
        name: 'Rebalancing',
        value: getRebalancingDisplayValue(portfolio?.rebalancingDefinition) ?? '-',
        different: false
      },
      {
        name: 'Portfolio Id',
        value: 'portfolioId' in portfolio ? (portfolio as any).portfolioId : '-',
        different: false
      },
      { name: 'Tax classification', value: portfolio?.taxClassification ?? '-', different: false },
      {
        name: 'Start date',
        value: (portfolio as ApiFundOfFundsPortfolio)?.startDate
          ? new Date((portfolio as ApiFundOfFundsPortfolio)?.startDate).toLocaleDateString(
              'en-GB',
              {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              }
            )
          : '-',
        different: false
      }
    ]

    return { propertyList, strategyList }
  }

  const { propertyList: currentPropertyList, strategyList: currentStrategyList } =
    generateDataLists(currentPortfolio)

  const removeProposedDateAddedIfNoCurrent = (
    currentPortfolio: Portfolio,
    proposedPortfolio: Portfolio
  ) => {
    if (
      !(currentPortfolio as ApiFundOfFundsPortfolio)?.startDate &&
      !!(proposedPortfolio as ApiFundOfFundsPortfolio)?.startDate
    ) {
      delete (proposedPortfolio as ApiFundOfFundsPortfolio).startDate
    }
    return proposedPortfolio
  }

  const { propertyList: proposedPropertyList, strategyList: proposedStrategyList } =
    generateDataLists(removeProposedDateAddedIfNoCurrent(currentPortfolio, proposedPortfolio))

  const setValueDifferences = (currentList: RowData[], proposedList: RowData[]) => {
    currentList.forEach((currentItem, index) => {
      const proposedItem = proposedList[index]
      if (proposedItem.value !== currentItem.value) {
        currentItem.different = true
        proposedItem.different = true
      }
    })
  }

  setValueDifferences(currentPropertyList, proposedPropertyList)
  setValueDifferences(currentStrategyList, proposedStrategyList)

  return (
    <SimpleModal>
      <ModalHeader onClickClose={onClose}>
        Approve or reject this {PORTFOLIO_TYPE[proposedPortfolio.type].value} portfolio
      </ModalHeader>
      <Space isflex flexDirection="column" p={6}>
        <StyledMessageBoxWrapper>
          <MessageBox state="warning">
            <Space isflex flexGrow={1} justifyContent="center">
              <Space isflex alignItems="center" ml={3}>
                <Space mr={1}>Current total value:</Space>
                <Typography fontSize="13px" fontWeight="bold" variant="b3">
                  {currentTotalValue ? formatUnits(currentTotalValue, 2) : 0}
                </Typography>
              </Space>
            </Space>
          </MessageBox>
        </StyledMessageBoxWrapper>
        <Space mt={5}>
          <StyledChangesModalTabsWrapper>
            <Tabs.List className="mt-4" spacing="md">
              <Tabs.Tab
                isSelected={selectedTab === 'details'}
                value="details"
                onClick={() => setSelectedTab('details')}
              >
                Details
              </Tabs.Tab>
              <Tabs.Tab
                isSelected={selectedTab === 'restrictions'}
                value="restrictions"
                onClick={() => setSelectedTab('restrictions')}
              >
                Restrictions and usage
              </Tabs.Tab>
            </Tabs.List>
          </StyledChangesModalTabsWrapper>
        </Space>
        {selectedTab === 'details' && (
          <Tabs.Panel value="details">
            <Space flexDirection="column" mt={5}>
              <ProposedChangesModalAssetsSection
                currentInvestmentWeights={
                  'investmentWeights' in currentPortfolio ? currentPortfolio.investmentWeights : []
                }
                currentWeights={currentPortfolio?.targetWeights ?? []}
                proposedInvestmentWeights={
                  'investmentWeights' in proposedPortfolio
                    ? proposedPortfolio.investmentWeights
                    : []
                }
                proposedWeights={proposedPortfolio?.targetWeights ?? []}
              />
              <Space isflex flexDirection="column" flexGrow={1}>
                <Space isflex flexGrow={1}>
                  <Space isflex flexBasis="50%" mr={3}>
                    <ProposedChangesModalDataBlock
                      dataList={currentPropertyList}
                      dataTestId="current-properties"
                    />
                  </Space>
                  <StyledVerticalDivider />
                  <Space isflex flexBasis="50%" ml={3}>
                    <ProposedChangesModalDataBlock
                      dataList={proposedPropertyList}
                      dataTestId="proposed-properties"
                    />
                  </Space>
                </Space>
                <ProposedChangesModalFeesSection
                  currentFees={currentPortfolio?.fees ?? []}
                  proposedFees={proposedPortfolio?.fees ?? []}
                />
                <Space isflex flexGrow={1}>
                  <Space isflex flexBasis="50%" mr={3}>
                    <ProposedChangesModalDataBlock
                      dataList={currentStrategyList}
                      dataTestId="current-strategies"
                    />
                  </Space>
                  <StyledVerticalDivider />
                  <Space isflex flexBasis="50%" ml={3}>
                    <ProposedChangesModalDataBlock
                      dataList={proposedStrategyList}
                      dataTestId="proposed-strategies"
                    />
                  </Space>
                </Space>
              </Space>
            </Space>
          </Tabs.Panel>
        )}
        {selectedTab === 'restrictions' && (
          <Tabs.Panel value="restrictions">
            <ProposedChangesRestrictionsTabContent />
          </Tabs.Panel>
        )}
      </Space>
      {(user?.roles?.includes('FLStructuring') || user?.roles?.includes('Administrator')) && (
        <ProposedChangesApproval
          canProceedApproval={canProceedApproval}
          portfolio={proposedPortfolio}
          onApprovePortfolio={onApprovePortfolio}
          onCancel={onClose}
          onChangeApprovalStatus={onChangeApprovalStatus}
          onChangeApprovalUserName={onChangeApprovalUserName}
        />
      )}
    </SimpleModal>
  )
}
